import { useEffect, useState } from 'react';

import useScreenSize from './useScreenSize';

const extractResolution = (str) => {
  if (!str) return { width: 0, height: 0 };
  const regex = /(\d+)x(\d+)/;
  const match = str.match(regex);
  return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) };
};

const defaultOptions = {
  quality: 60,
  formats: ['webp', 'jpeg'],
  size: null,
  blurSize: 100,
};

const selectImageSizes = (screenSizes) => {
  const { isMobile, isTablet, isDesktop, isLargeDesktop, isExtraLargeDesktop } = screenSizes;
  const size = isExtraLargeDesktop
    ? 2560
    : isLargeDesktop
      ? 1920
      : isDesktop
        ? 1280
        : isTablet
          ? 640
          : isMobile
            ? 640
            : 1280;

  let blurSize = 100;
  if (size >= blurSize) {
    blurSize = size / 2;
  }
  return { size, blurSize };
};

// create types for options
/**
 * @param {string} fileName - The file name.
 * @param {object} options - The options.
 * @param {number} options.quality - The quality.
 * @param {string[]} options.formats - The formats.
 * @param {number} options.size - The size.
 * @param {number} options.blurSize - The blur size.
 * @returns {{sources: *[], src: string, blurSources: *[], blurSrc: string}|{sources: unknown[], src, blurSources: *, blurSrc: string}} - The sources and blur sources.
 */
const useStoryblokImage = (
  fileName,
  options = { quality: 60, formats: ['webp', 'jpeg'], blurSize: 100 },
) => {
  const optionsToUse = { ...defaultOptions, ...options };
  const screenSizes = useScreenSize();
  const [dimensions, setDimensions] = useState(extractResolution(fileName));

  useEffect(() => {
    if (fileName) setDimensions(extractResolution(fileName));
  }, [fileName]);

  if (!fileName) return { sources: [], blurSources: [], src: '', blurSrc: '' };

  const { width, height } = dimensions;
  const isVertical = height > width;

  const size = optionsToUse.size ? optionsToUse.size : selectImageSizes(screenSizes).size;
  const blurSize = optionsToUse.blurSize
    ? optionsToUse.blurSize
    : selectImageSizes(screenSizes).blurSize;

  const imageSize = isVertical ? `0x${size}` : `${size}x0`;
  const sources = fileName
    ? optionsToUse.formats.map((format, index) => {
      return (
        <source
          key={index}
          srcSet={`${fileName}/m/${imageSize}/filters:format(${format}):quality(${optionsToUse.quality})`}
          type={`image/${format}`}
        />
      );
    })
    : [];

  const blurImageSize = isVertical ? `0x${blurSize}` : `${blurSize}x0`;
  const blurSources = fileName
    ? optionsToUse.formats.map((format, index) => {
      return (
        <source
          key={index}
          srcSet={`${fileName}/m/${blurImageSize}/filters:format(${format}):quality(${optionsToUse.quality})`}
          type={`image/${format}`}
        />
      );
    })
    : [];
  return {
    sources,
    blurSources,
    src: `${fileName}/m/${imageSize}/filters:format(${optionsToUse.formats[0]}):quality(${optionsToUse.quality})`,
    blurSrc: `${fileName}/m/${blurImageSize}/filters:format(${optionsToUse.formats[0]}):quality(${optionsToUse.quality})`,
  };
};

export default useStoryblokImage;
